/**
 * @fileoverview Module de gestion de la liste de souhaits d'achat.
 * Orchestre le rendu du tableau, le formulaire d'ajout/édition dans une <dialog>
 * et le pont vers la collection via le callback onMoveToCollection.
 *
 * Usage :
 *   - `initWishlist(callbacks)` — une seule fois au démarrage.
 *   - `renderWishlist(items)` — pour afficher / rafraîchir la liste.
 *   - `openAddWishDialog()` — pour ouvrir le formulaire d'ajout (bouton sidebar).
 *
 * Note : la recherche ISBN ne propose pas de caméra. La wishlist cible les BD non
 * encore en main (ouvrage repéré, pas scannable) ; la saisie manuelle est suffisante.
 */

import { addWishItem, updateWishItem, deleteWishItem, findWishItemByIsbn } from './db.js';
import { fetchBookData } from './api-books.js';
import { showLoader, hideLoader, showNotification } from './ui.js';
import { formatDate } from './utils.js';
import { loadScannerLibrary } from './scanner.js';
import { openDialog, closeDialog } from './dialogs.js';

/** @type {number|null} Identifiant du souhait en cours d'édition (null = création). */
let _currentEditingId = null;

/**
 * Callback fourni par app.js — déclenché quand l'utilisateur veut déplacer un souhait
 * vers la collection. L'appelant est responsable de supprimer le souhait après
 * que la sauvegarde dans la collection a réussi.
 * @type {Function|null}
 */
let _onMoveToCollection = null;

/**
 * Callback fourni par app.js — déclenché après chaque mutation (ajout, modif, suppression)
 * pour que app.js puisse relancer le rendu de la wishlist.
 * @type {Function|null}
 */
let _onDataChanged = null;

// ============================================================
// --- API PUBLIQUE ---
// ============================================================

/**
 * Initialise le module wishlist : branche le formulaire, la recherche ISBN et les
 * écouteurs du dialog. Doit être appelée une seule fois au démarrage, DOM prêt.
 *
 * @param {Object} callbacks
 * @param {Function} callbacks.onDataChanged - Appelé après chaque mutation pour rafraîchir la vue.
 * @param {Function} callbacks.onMoveToCollection - Appelé avec l'item wishlist quand l'utilisateur
 *   veut le déplacer dans la collection.
 */
export function initWishlist({ onDataChanged, onMoveToCollection }) {
    _onDataChanged = onDataChanged;
    _onMoveToCollection = onMoveToCollection;

    _bindIsbnSearch();
    _bindWishImageScan();
    _bindForm();
}

/**
 * Rend la liste de souhaits dans `#wish-list` via les templates HTML dédiés.
 * Affiche un état vide si la liste est vide.
 *
 * @param {Object[]} items - Tableau des souhaits à afficher (déjà triés si nécessaire).
 */
export function renderWishlist(items) {
    const container = document.getElementById('wish-list');
    const tableTemplate = document.getElementById('wish-table-template');
    const rowTemplate = document.getElementById('wish-row-template');

    if (!container || !tableTemplate || !rowTemplate) return;

    container.innerHTML = '';

    if (items.length === 0) {
        const msg = document.createElement('p');
        msg.className = 'wish-empty';
        msg.textContent = 'Aucun souhait pour le moment. Ajoute ta première envie !';
        container.appendChild(msg);
        return;
    }

    const tableClone = tableTemplate.content.cloneNode(true);
    const tbody = tableClone.querySelector('tbody');

    for (const item of items) {
        try {
            const rowClone = rowTemplate.content.cloneNode(true);
            _fillRow(rowClone, item);
            tbody.appendChild(rowClone);
        } catch (err) {
            console.error('Impossible d\'afficher le souhait ID : ' + item.id, err);
        }
    }

    container.appendChild(tableClone);
}

/**
 * Ouvre le dialog d'ajout ou d'édition d'un souhait.
 * Sans argument → mode création ; avec un item → mode édition pré-rempli.
 *
 * @param {Object} [item] - Le souhait à éditer. Omis pour une création.
 */
export function openAddWishDialog(item) {
    if (item) {
        _currentEditingId = item.id;
        _fillForm(item);
        document.getElementById('wish-form-title').textContent = 'Modifier le souhait';
    } else {
        _currentEditingId = null;
        _resetForm();
        document.getElementById('wish-form-title').textContent = 'Nouveau souhait';
    }

    openDialog('add-wish-dialog');
    document.getElementById('wish-input-title')?.focus();
}

// ============================================================
// --- PRIVÉ ---
// ============================================================

/**
 * Branche le scan ISBN depuis un fichier image dans le dialog wishlist.
 * Réutilise loadScannerLibrary() pour le chargement différé de html5-qrcode.
 * Le flow est identique à _bindIsbnSearch mais part d'un fichier image plutôt que d'une saisie.
 * @private
 */
function _bindWishImageScan() {
    const input = document.getElementById('input-wish-isbn-image');
    if (!input) return;

    input.addEventListener('change', async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            await loadScannerLibrary();
        } catch {
            showNotification('Impossible de charger le scanner.', 'error');
            input.value = '';
            return;
        }

        showLoader('Analyse de l\'image…');
        let isbn;
        try {
            const scanner = new Html5Qrcode('camera-reader');
            isbn = await scanner.scanFile(file, /* showImage= */ false);
        } catch {
            hideLoader();
            showNotification('Aucun code-barres détecté dans cette image.', 'error');
            input.value = '';
            return;
        }

        // Même pipeline que la recherche ISBN manuelle
        let result;
        try {
            result = await fetchBookData(isbn);
        } finally {
            hideLoader();
        }

        if (result) {
            _fillForm({ isbn, title: result.title, series: result.series, volume: result.volume, authors: result.authors, publisher: result.publisher });
            showNotification('Informations récupérées !', 'success');
        } else {
            document.getElementById('wish-input-isbn').value = isbn;
            showNotification(`Aucune information trouvée pour l'ISBN ${isbn}.`, 'error');
        }

        input.value = '';
    });
}

/**
 * Branche la recherche ISBN manuelle du dialog wishlist.
 * Appelle fetchBookData directement (pas de caméra) et pré-remplit le formulaire.
 * @private
 */
function _bindIsbnSearch() {
    const btn   = document.getElementById('btn-wish-search-isbn');
    const input = document.getElementById('input-wish-isbn-search');

    btn?.addEventListener('click', async () => {
        const isbn = input.value.trim();
        if (!isbn) return;

        showLoader('Recherche en cours…');
        let result;
        try {
            result = await fetchBookData(isbn);
        } finally {
            hideLoader();
        }

        if (result) {
            _fillForm({
                isbn:      isbn,
                title:     result.title,
                series:    result.series,
                volume:    result.volume,
                authors:   result.authors,
                publisher: result.publisher,
            });
            showNotification('Informations récupérées !', 'success');
        } else {
            // Conserver au moins l'ISBN saisi pour ne pas le perdre
            document.getElementById('wish-input-isbn').value = isbn;
            showNotification(`Aucune information trouvée pour l'ISBN ${isbn}.`, 'error');
        }

        input.value = '';
    });
}

/**
 * Branche la soumission du formulaire wishlist, le bouton Annuler
 * et la fermeture native du dialog (touche Échap).
 * @private
 */
function _bindForm() {
    const form      = document.getElementById('add-wish-form');
    const btnCancel = document.getElementById('btn-wish-cancel');
    const dialog    = document.getElementById('add-wish-dialog');

    if (!form || !btnCancel || !dialog) return;

    btnCancel.addEventListener('click', () => {
        _resetForm();
        closeDialog('add-wish-dialog');
    });

    dialog.addEventListener('cancel', () => _resetForm());

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const wishData = {
            title:     document.getElementById('wish-input-title').value.trim()     || null,
            series:    document.getElementById('wish-input-series').value.trim()    || null,
            volume:    parseInt(document.getElementById('wish-input-volume').value, 10) || null,
            authors:   _splitList(document.getElementById('wish-input-authors').value),
            publisher: document.getElementById('wish-input-publisher').value.trim() || null,
            isbn:      document.getElementById('wish-input-isbn').value.trim()      || null,
            note:      document.getElementById('wish-input-note').value.trim()      || null,
        };

        try {
            if (_currentEditingId) {
                await updateWishItem({ ...wishData, id: _currentEditingId });
            } else {
                // Déduplication ISBN avant insertion
                if (wishData.isbn) {
                    const existing = await findWishItemByIsbn(wishData.isbn);
                    if (existing) {
                        const label = existing.series || existing.title || 'cette BD';
                        showNotification(`"${label}" est déjà dans ta liste de souhaits.`, 'error');
                        return;
                    }
                }
                await addWishItem(wishData);
            }

            _resetForm();
            closeDialog('add-wish-dialog');
            _onDataChanged?.();
        } catch (err) {
            console.error('Erreur lors de la sauvegarde du souhait :', err);
            showNotification('Impossible de sauvegarder le souhait.', 'error');
        }
    });
}

/**
 * Remplit les cellules d'une ligne du tableau wishlist et branche les trois actions
 * (modifier, supprimer, déplacer vers la collection).
 * @private
 * @param {DocumentFragment} clone - Fragment cloné depuis `#wish-row-template`.
 * @param {Object} item - Le souhait à afficher.
 */
function _fillRow(clone, item) {
    const setCell = (selector, value) => {
        const el = clone.querySelector(selector);
        if (el) el.textContent = value ?? '';
    };

    setCell('.col-wish-title', item.title);

    const seriesParts = [];
    if (item.series) {
        seriesParts.push(item.series);
        if (item.volume) seriesParts.push(`Tome ${item.volume}`);
    }
    setCell('.col-wish-series', seriesParts.join(' - '));

    setCell('.col-wish-authors',   Array.isArray(item.authors) ? item.authors.join(', ') : (item.authors ?? ''));
    setCell('.col-wish-publisher', item.publisher);
    setCell('.col-wish-isbn',      item.isbn);
    setCell('.col-wish-note',      item.note);
    setCell('.col-wish-addedat',   formatDate(item.addedAt));

    clone.querySelector('.btn-wish-edit').addEventListener('click', (e) => {
        e.stopPropagation();
        openAddWishDialog(item);
    });

    clone.querySelector('.btn-wish-delete').addEventListener('click', async (e) => {
        e.stopPropagation();
        const label = item.series || item.title || 'ce souhait';
        if (!confirm(`Supprimer "${label}" de ta liste de souhaits ?`)) return;

        try {
            await deleteWishItem(item.id);
            _onDataChanged?.();
        } catch (err) {
            console.error('Erreur lors de la suppression du souhait :', err);
            showNotification('Impossible de supprimer le souhait.', 'error');
        }
    });

    clone.querySelector('.btn-wish-collect').addEventListener('click', (e) => {
        e.stopPropagation();
        _onMoveToCollection?.(item);
    });
}

/**
 * Pré-remplit les champs du formulaire wishlist avec les données d'un item ou d'un résultat d'API.
 * @private
 * @param {Object} data
 */
function _fillForm(data) {
    const set = (id, val) => {
        const el = document.getElementById(id);
        if (el) el.value = val ?? '';
    };

    set('wish-input-title',     data.title);
    set('wish-input-series',    data.series);
    set('wish-input-volume',    data.volume);
    set('wish-input-authors',   Array.isArray(data.authors) ? data.authors.join(', ') : (data.authors ?? ''));
    set('wish-input-publisher', data.publisher);
    set('wish-input-isbn',      data.isbn);
    set('wish-input-note',      data.note);
}

/**
 * Réinitialise le formulaire wishlist et efface l'identifiant d'édition.
 * @private
 */
function _resetForm() {
    document.getElementById('add-wish-form')?.reset();
    _currentEditingId = null;
}

/**
 * Découpe une chaîne séparée par des virgules en tableau de valeurs non-vides.
 * Retourne null si la chaîne est vide ou ne contient que des espaces.
 * @private
 * @param {string} value
 * @returns {string[]|null}
 */
function _splitList(value) {
    if (!value?.trim()) return null;
    const parts = value.split(',').map(s => s.trim()).filter(Boolean);
    return parts.length ? parts : null;
}
